import { formatMoney } from "@/pages/InvoiceManagement/utils/money";
import type { ResponseMode, RfqLineRate, VendorRfqBase, VendorRfqDecline, VendorRfqDetail, VendorRfqLine, VendorRfqSubmission } from "../types";

export type RateMap = Record<string, string>;

export interface LineQuote {
  line_id: number;
  rate: number | null;
  error?: string;
  amount: number;
  delta: number;
  deltaPct: number;
  changed: boolean;
}

export interface ResponseSummary {
  lines: LineQuote[];
  total: number;
  askTotal: number;
  delta: number;
  deltaPct: number;
  mode: ResponseMode;
  changed: boolean;
  valid: boolean;
}

type WithResponse = Partial<Pick<VendorRfqBase, "my_response">>;

export const RATE_PATTERN = /^\d+(\.\d{1,2})?$/;

const round2 = (value: number): number => Math.round(value * 100) / 100;

const pct = (delta: number, base: number): number => (base === 0 ? 0 : (delta / base) * 100);

export function initialRates(detail: VendorRfqDetail): RateMap {
  return Object.fromEntries(detail.lines.map((line) => [line.line_id, String(line.my_bid?.rate ?? line.ask_rate)]));
}

export function parseRate(input: string): { value: number | null; error?: string } {
  const trimmed = input.trim();
  if (!trimmed) return { value: null, error: "Enter a rate" };
  if (!RATE_PATTERN.test(trimmed)) return { value: null, error: "Use numbers with up to two decimals" };
  const value = Number(trimmed);
  if (!(value > 0)) return { value: null, error: "Rate must be more than zero" };
  return { value };
}

function quoteLine(line: VendorRfqLine, input: string | undefined): LineQuote {
  const { value, error } = parseRate(input ?? "");
  if (value === null) return { line_id: line.line_id, rate: null, error, amount: 0, delta: 0, deltaPct: 0, changed: false };
  const amount = round2(value * line.quantity);
  const delta = round2(amount - line.ask_amount);
  return { line_id: line.line_id, rate: value, amount, delta, deltaPct: pct(delta, line.ask_amount), changed: value !== line.ask_rate };
}

export function summarise(detail: VendorRfqDetail, rates: RateMap): ResponseSummary {
  const lines = detail.lines.map((line) => quoteLine(line, rates[line.line_id]));
  const total = round2(lines.reduce((sum, line) => sum + line.amount, 0));
  const delta = round2(lines.reduce((sum, line) => sum + line.delta, 0));
  const changed = lines.some((line) => line.changed);
  return {
    lines,
    total,
    askTotal: detail.ask_total,
    delta,
    deltaPct: pct(delta, detail.ask_total),
    mode: changed ? "BID" : "ACCEPT_AT_ASK",
    changed,
    valid: lines.every((line) => line.rate !== null),
  };
}

export function toPayload(summary: ResponseSummary): RfqLineRate[] {
  if (!summary.valid) return [];
  return summary.lines.map((line) => ({ line_id: line.line_id, rate: line.rate as number }));
}

export function describeDelta(delta: number): string {
  if (delta === 0) return "at ask price";
  return delta < 0 ? "under ask price" : "over ask price";
}

const sign = (value: number): string => (value < 0 ? "−" : value > 0 ? "+" : "");

export function formatSignedMoney(delta: number): string {
  return `${sign(delta)}${formatMoney(Math.abs(delta))}`;
}

export function formatPct(value: number): string {
  return `${sign(value)}${Math.abs(value).toFixed(1)}%`;
}

export function tradesOf(rfq: Pick<VendorRfqBase, "trades">): string {
  return rfq.trades.join(" · ");
}

export function submissionOf(rfq: WithResponse): VendorRfqSubmission | null {
  return rfq.my_response && rfq.my_response.mode ? rfq.my_response : null;
}

export function declineOf(rfq: WithResponse): VendorRfqDecline | null {
  return rfq.my_response && rfq.my_response.mode === null ? rfq.my_response : null;
}
